import React, { useState, useEffect } from 'react';
import {
    Form, Input, Select, Button, Divider, Typography, Space,
    InputNumber, Collapse, Tag, Popconfirm, Switch
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, SaveOutlined,
    StarOutlined,
} from '@ant-design/icons';
import { useFlowStore } from '../../stores/flow-store-context';
import ModuleTags from '../common/module-tags';
import apiCore from '../../utils/api-core';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

const REPLY_TYPES = [
    { value: '', label: 'n/a (one-way)' },
    { value: 'keyword-fork', label: 'keyword-fork' },
    { value: 'email', label: 'email' },
    { value: 'date', label: 'date' },
    { value: 'time', label: 'time' },
    { value: 'number', label: 'number' },
    { value: 'options', label: 'options' },
    { value: 'cards', label: 'cards' },
    { value: 'wit-ai', label: 'wit-ai' },
    { value: 'zip-recruiter', label: 'zip-recruiter' },
    { value: 'zip-code', label: 'zip-code' },
    { value: 'send-details', label: 'send-details' },
    { value: 'send-details-grp', label: 'send-details-grp' },
    { value: 'send-details-all', label: 'send-details-all' },
    { value: 'dept-location', label: 'dept-location' },
    { value: 'career-type', label: 'career-type' },
    { value: 'business-type', label: 'business-type' },
    { value: 'event', label: 'event' },
    { value: 'calendly', label: 'calendly' },
    { value: 'time-matching', label: 'time-matching' },
    { value: 'event-cancel', label: 'event-cancel (cancel booked interview)' },
];

// module.email_groups may arrive as a JS array, a Postgres-array literal string
// ('{"id1","id2"}' or "{id1,id2}"), "{}", or null. Always normalize to a clean
// string[] of ids (Postgres quotes array elements, so strip surrounding quotes).
const parseEmailGroups = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.length && raw !== '{}') {
        return raw
            .replace(/^{/, '').replace(/}$/, '')
            .split(',')
            .map((s) => s.trim().replace(/^"|"$/g, ''))
            .filter(Boolean);
    }
    return [];
};

// ── Connections Sub-Panel ─────────────────────────────
const ConnectionsSection = ({ module, modules }) => {
    const { createConnection, deleteConnection } = useFlowStore();
    const [newFilters, setNewFilters] = useState('');
    const [newGoto, setNewGoto] = useState('');
    // Intent hints (ADR-033): what each filter resolves to, redundant variants, ambiguity.
    const [hints, setHints] = useState({ filters: [], ambiguous: [] });

    const connections = module.connection || [];
    const filtersKey = connections.map((c) => c.filters).join('');

    useEffect(() => {
        if (!connections.length) { setHints({ filters: [], ambiguous: [] }); return undefined; }
        let cancelled = false;
        apiCore.post('/api/v3/intents/resolve', { filters: connections.map((c) => c.filters || '') })
            .then((res) => {
                if (cancelled) return;
                const d = (res && res.data) || {};
                setHints({ filters: d.filters || [], ambiguous: d.ambiguous || [] });
            })
            .catch(() => { if (!cancelled) setHints({ filters: [], ambiguous: [] }); });
        return () => { cancelled = true; };
    }, [filtersKey]); // connections identity changes every fetch; the joined filters string is the real dependency

    const handleCreate = async () => {
        if (!newFilters.trim() || !newGoto) return;
        await createConnection(module.id, newGoto, newFilters.trim());
        setNewFilters('');
        setNewGoto('');
    };

    // No update endpoint for connections — Tidy is delete + re-create with the distinct tokens.
    const handleTidy = async (conn, tidy) => {
        await deleteConnection(module.id, conn.goto);
        await createConnection(module.id, conn.goto, tidy);
    };

    return (
        <div className="detail-section">
            {connections.length === 0 && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '4px 0' }}>
                    No connections yet
                </Text>
            )}
            {hints.ambiguous.length > 0 && (
                <Text type="danger" style={{ display: 'block', fontSize: 11, margin: '4px 0 8px' }}>
                    {hints.ambiguous.map((a) => a.intent).join(', ')} appears on more than one branch — the dictionary
                    will not choose between them; only the exact words will match.
                </Text>
            )}
            {connections.map((conn, idx) => {
                const targetMod = modules.find((m) => m.id === conn.goto);
                const hint = hints.filters[idx];
                return (
                    <div key={conn.goto}>
                        <div className="connection-row">
                            <Tag color="blue" style={{ fontSize: 11 }}>{conn.filters}</Tag>
                            <Text style={{ fontSize: 12 }}>→ {targetMod ? targetMod.title : 'Unknown'}</Text>
                            <Popconfirm
                                title="Delete this connection?"
                                onConfirm={() => deleteConnection(module.id, conn.goto)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                            </Popconfirm>
                        </div>
                        {hint && (hint.intents.length > 0 || hint.redundant > 0) && (
                            <div style={{ margin: '-2px 0 6px 4px', fontSize: 11 }}>
                                {hint.intents.map((i) => (
                                    <Tag key={i} style={{ fontSize: 10 }}>→ {i}</Tag>
                                ))}
                                {hint.redundant > 0 && (
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        {hint.redundant} redundant variant{hint.redundant > 1 ? 's' : ''} — “{hint.tidy}” alone is enough.{' '}
                                        <Button type="link" size="small" style={{ padding: 0, fontSize: 11 }} onClick={() => handleTidy(conn, hint.tidy)}>
                                            Tidy
                                        </Button>
                                    </Text>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
            <Divider style={{ margin: '8px 0' }} />
            <Space.Compact style={{ width: '100%' }}>
                <Input
                    placeholder="One word per answer, e.g. yes — variants and punctuation are handled"
                    value={newFilters}
                    onChange={(e) => setNewFilters(e.target.value)}
                    style={{ flex: 1 }}
                />
                <Select
                    placeholder="Go to..."
                    value={newGoto || undefined}
                    onChange={setNewGoto}
                    style={{ width: 140 }}
                >
                    {modules
                        .filter((m) => m.id !== module.id)
                        .map((m) => (
                            <Option key={m.id} value={m.id}>{m.title}</Option>
                        ))}
                </Select>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                    Add
                </Button>
            </Space.Compact>
        </div>
    );
};

// ── Follow-ups Sub-Panel ──────────────────────────────
const FollowupsSection = ({ module }) => {
    const { updateModule } = useFlowStore();
    const followups = module.followups || [];

    const [newMsg, setNewMsg] = useState('');
    const [newTime, setNewTime] = useState(null);

    const saveFollowups = async (updatedFollowups) => {
        await updateModule(module.id, {
            title: module.title,
            reply_type: module.reply_type || '',
            response: module.response,
            options: module.options,
            quick_replies: module.quick_replies,
            error_response: module.error_response || '',
            fallback: module.fallback || '',
            field: module.field || '',
            email_groups: module.email_groups,
            followups: JSON.stringify(updatedFollowups),
        });
    };

    const handleAddFollowup = async () => {
        if (!newMsg.trim() || !newTime || newTime <= 0 || newTime > 720) return;
        const updated = [...followups, {
            id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`,
            message: newMsg.trim(),
            time: newTime,
        }];
        await saveFollowups(updated);
        setNewMsg('');
        setNewTime(null);
    };

    const handleDeleteFollowup = async (followupId) => {
        const updated = followups.filter((f) => f.id !== followupId);
        await saveFollowups(updated);
    };

    return (
        <div className="detail-section">
            {followups.length === 0 && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '4px 0' }}>
                    No follow-ups
                </Text>
            )}
            {followups.map((f) => (
                <div key={f.id} className="followup-row">
                    <Tag color="orange" style={{ fontSize: 11 }}>{f.time}h</Tag>
                    <Text ellipsis style={{ flex: 1, fontSize: 12 }}>{f.message}</Text>
                    <Popconfirm
                        title="Delete this follow-up?"
                        onConfirm={() => handleDeleteFollowup(f.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </div>
            ))}
            <Divider style={{ margin: '8px 0' }} />
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Space.Compact style={{ width: '100%' }}>
                    <InputNumber
                        placeholder="Hours"
                        min={1}
                        max={720}
                        value={newTime}
                        onChange={setNewTime}
                        style={{ width: 80 }}
                    />
                    <Input
                        placeholder="Follow-up message..."
                        value={newMsg}
                        onChange={(e) => setNewMsg(e.target.value)}
                        style={{ flex: 1 }}
                    />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddFollowup}>
                        Add
                    </Button>
                </Space.Compact>
            </Space>
        </div>
    );
};

// ── Triggers Sub-Panel ───────────────────────────────
const TRIGGER_TYPES = [
    { value: 'no_reply_timeout', label: 'No Reply Timeout' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'ats_status_change', label: 'ATS Status Change' },
];

const TRIGGER_COLORS = {
    no_reply_timeout: 'red',
    scheduled: 'purple',
    ats_status_change: 'volcano',
};

const formatDelayMinutes = (totalMinutes) => {
    if (totalMinutes >= 1440 && totalMinutes % 1440 === 0) return `${totalMinutes / 1440}d`;
    if (totalMinutes >= 60 && totalMinutes % 60 === 0) return `${totalMinutes / 60}h`;
    return `${totalMinutes}min`;
};

const getConfigSummary = (trigger) => {
    const config = trigger.config || {};
    switch (trigger.trigger_type) {
        case 'no_reply_timeout':
            return formatDelayMinutes(config.timeout_minutes || 60);
        case 'scheduled':
            return `${formatDelayMinutes(config.delay_minutes || 60)} delay`;
        case 'ats_status_change':
            return config.to_status || '(any)';
        default:
            return '';
    }
};

const TriggersSection = ({ module, modules }) => {
    const { createTrigger, deleteTrigger } = useFlowStore();
    const [triggerType, setTriggerType] = useState('');
    const [gotoModuleId, setGotoModuleId] = useState('');
    const [timeoutValue, setTimeoutValue] = useState(60);
    const [timeoutUnit, setTimeoutUnit] = useState('min');
    const [delayValue, setDelayValue] = useState(60);
    const [delayUnit, setDelayUnit] = useState('min');
    const [toStatus, setToStatus] = useState('');

    const triggers = (module.triggers || []).filter((t) => !t.deleted_at);

    const toMinutes = (value, unit) => {
        const multiplier = unit === 'days' ? 1440 : unit === 'hours' ? 60 : 1;
        return (value || 1) * multiplier;
    };

    const buildConfig = () => {
        switch (triggerType) {
            case 'no_reply_timeout':
                return { timeout_minutes: toMinutes(timeoutValue, timeoutUnit) };
            case 'scheduled':
                return { delay_minutes: toMinutes(delayValue, delayUnit) };
            case 'ats_status_change':
                return { to_status: toStatus };
            default:
                return {};
        }
    };

    const handleCreate = async () => {
        if (!triggerType || !gotoModuleId) return;
        await createTrigger(module.id, gotoModuleId, triggerType, buildConfig());
        setTriggerType('');
        setGotoModuleId('');
        setTimeoutValue(60);
        setTimeoutUnit('min');
        setDelayValue(60);
        setDelayUnit('min');
        setToStatus('');
    };

    const renderDurationInput = (value, setValue, unit, setUnit, placeholder) => (
        <Space.Compact style={{ width: '100%' }}>
            <InputNumber
                placeholder={placeholder}
                value={value}
                onChange={setValue}
                style={{ flex: 1 }}
            />
            <Select
                value={unit}
                onChange={setUnit}
                style={{ width: 90 }}
            >
                <Option value="min">min</Option>
                <Option value="hours">hours</Option>
                <Option value="days">days</Option>
            </Select>
        </Space.Compact>
    );

    const renderConfigInput = () => {
        switch (triggerType) {
            case 'no_reply_timeout':
                return renderDurationInput(timeoutValue, setTimeoutValue, timeoutUnit, setTimeoutUnit, 'Timeout');
            case 'scheduled':
                return renderDurationInput(delayValue, setDelayValue, delayUnit, setDelayUnit, 'Delay');
            case 'ats_status_change':
                return (
                    <Input
                        placeholder="To status (e.g. interview)"
                        value={toStatus}
                        onChange={(e) => setToStatus(e.target.value)}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="detail-section">
            {triggers.length === 0 && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '4px 0' }}>
                    No triggers yet
                </Text>
            )}
            {triggers.map((trigger) => {
                const targetMod = modules.find((m) => m.id === trigger.goto_module_id);
                return (
                    <div key={trigger.id} className="trigger-row">
                        <Tag color={TRIGGER_COLORS[trigger.trigger_type] || 'default'} style={{ fontSize: 10 }}>
                            {(TRIGGER_TYPES.find((t) => t.value === trigger.trigger_type) || {}).label || trigger.trigger_type}
                        </Tag>
                        <Text style={{ fontSize: 11 }}>{getConfigSummary(trigger)}</Text>
                        <Text style={{ fontSize: 12 }}>
                            → {targetMod ? targetMod.title : 'Unknown'}
                        </Text>
                        <Popconfirm
                            title="Delete this trigger?"
                            onConfirm={() => deleteTrigger(trigger.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </div>
                );
            })}
            <Divider style={{ margin: '8px 0' }} />
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Select
                    placeholder="Trigger type"
                    value={triggerType || undefined}
                    onChange={setTriggerType}
                    style={{ width: '100%' }}
                >
                    {TRIGGER_TYPES.map((t) => (
                        <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                </Select>
                {triggerType && renderConfigInput()}
                <Space.Compact style={{ width: '100%' }}>
                    <Select
                        placeholder="Go to module..."
                        value={gotoModuleId || undefined}
                        onChange={setGotoModuleId}
                        style={{ flex: 1 }}
                    >
                        {modules.map((m) => (
                            <Option key={m.id} value={m.id}>{m.title}</Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                        disabled={!triggerType || !gotoModuleId}
                    >
                        Add
                    </Button>
                </Space.Compact>
            </Space>
        </div>
    );
};

// ── Path Forks Sub-Panel (bot only) ───────────────────
const PathForksSection = ({ module, modules }) => {
    const { updatePathForks, companyDepartments = [] } = useFlowStore();
    const [newDept, setNewDept] = useState('');
    const [newGoto, setNewGoto] = useState('');

    const pathForks = Array.isArray(module.path_forks) ? module.path_forks : [];

    const handleAdd = async () => {
        if (!newDept || !newGoto) return;
        const dept = companyDepartments.find((d) => d.id === newDept);
        const target = modules.find((m) => m.id === newGoto);
        if (!dept || !target) return;
        const next = [
            ...pathForks,
            {
                department: { id: dept.id, label: dept.name },
                module: { id: target.id, label: target.title },
            },
        ];
        await updatePathForks(module.id, next);
        setNewDept('');
        setNewGoto('');
    };

    const handleRemove = async (fork) => {
        const next = pathForks.filter(
            (f) => !(f.department?.id === fork.department?.id && f.module?.id === fork.module?.id),
        );
        await updatePathForks(module.id, next);
    };

    return (
        <div className="detail-section">
            {pathForks.length === 0 && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '4px 0' }}>
                    No forks yet
                </Text>
            )}
            {pathForks.map((fork, idx) => (
                <div key={`${fork.department?.id}-${fork.module?.id}-${idx}`} className="connection-row">
                    <Tag color="purple" style={{ fontSize: 11 }}>{fork.department?.label || '—'}</Tag>
                    <Text style={{ fontSize: 12 }}>→ {fork.module?.label || 'Unknown'}</Text>
                    <Popconfirm
                        title="Delete this fork?"
                        onConfirm={() => handleRemove(fork)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </div>
            ))}
            <Divider style={{ margin: '8px 0' }} />
            <Space.Compact style={{ width: '100%' }}>
                <Select
                    placeholder="Department"
                    value={newDept || undefined}
                    onChange={setNewDept}
                    style={{ flex: 1 }}
                    disabled={companyDepartments.length === 0}
                >
                    {companyDepartments.map((d) => (
                        <Option key={d.id} value={d.id}>{d.name}</Option>
                    ))}
                </Select>
                <Select
                    placeholder="Go to..."
                    value={newGoto || undefined}
                    onChange={setNewGoto}
                    style={{ width: 140 }}
                >
                    {modules.filter((m) => m.id !== module.id).map((m) => (
                        <Option key={m.id} value={m.id}>{m.title}</Option>
                    ))}
                </Select>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>Add</Button>
            </Space.Compact>
        </div>
    );
};

// ── Bot Redirects Sub-Panel (bot only) ────────────────
const BotRedirectsSection = ({ module }) => {
    const { updateBotRedirect, botList = [], bot } = useFlowStore();
    const [newKeyword, setNewKeyword] = useState('');
    const [newBot, setNewBot] = useState('');

    const redirects = Array.isArray(module.bot_redirect) ? module.bot_redirect : [];
    const availableBots = botList.filter((b) => !bot || b.id !== bot.id);

    const handleAdd = async () => {
        if (!newKeyword.trim() || !newBot) return;
        const targetBot = botList.find((b) => b.id === newBot);
        if (!targetBot) return;
        const next = [
            ...redirects,
            {
                id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}`,
                keyword: newKeyword.trim(),
                bot: { id: targetBot.id, name: targetBot.name },
            },
        ];
        await updateBotRedirect(module.id, next);
        setNewKeyword('');
        setNewBot('');
    };

    const handleRemove = async (redirect) => {
        const next = redirects.filter((r) => r.id !== redirect.id);
        await updateBotRedirect(module.id, next);
    };

    return (
        <div className="detail-section">
            {redirects.length === 0 && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '4px 0' }}>
                    No redirects yet
                </Text>
            )}
            {redirects.map((redirect) => (
                <div key={redirect.id} className="connection-row">
                    <Tag color="blue" style={{ fontSize: 11 }}>{redirect.keyword}</Tag>
                    <Text style={{ fontSize: 12 }}>↪ {redirect.bot?.name || 'Unknown'}</Text>
                    <Popconfirm
                        title="Delete this redirect?"
                        onConfirm={() => handleRemove(redirect)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </div>
            ))}
            <Divider style={{ margin: '8px 0' }} />
            <Space.Compact style={{ width: '100%' }}>
                <Input
                    placeholder="Keyword"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    style={{ flex: 1 }}
                />
                <Select
                    placeholder="Bot"
                    value={newBot || undefined}
                    onChange={setNewBot}
                    style={{ width: 160 }}
                >
                    {availableBots.map((b) => (
                        <Option key={b.id} value={b.id}>{b.name}</Option>
                    ))}
                </Select>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>Add</Button>
            </Space.Compact>
        </div>
    );
};

// ── Tags Sub-Panel (bot only) ─────────────────────────
const TAG_TYPES = [
    { value: 'custom', label: 'custom' },
    { value: 'user-input-transform', label: 'user-input-transform' },
];

const TagsSection = ({ module }) => {
    const { addTag, deleteTag } = useFlowStore();
    const [newType, setNewType] = useState('custom');
    const [newFrom, setNewFrom] = useState('');
    const [newTo, setNewTo] = useState('');

    const tags = Array.isArray(module.tags) ? module.tags : [];

    const handleAdd = async () => {
        if (!newType) return;
        if (newType === 'custom' && !newTo.trim()) return;
        if (newType === 'user-input-transform' && (!newFrom.trim() || !newTo.trim())) return;
        await addTag(module.id, newType, newFrom.trim() || null, newTo.trim() || null);
        setNewFrom('');
        setNewTo('');
    };

    return (
        <div className="detail-section">
            {tags.length === 0 && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '4px 0' }}>
                    No tags yet
                </Text>
            )}
            {tags.map((tag) => (
                <div key={tag.id} className="connection-row">
                    <Tag color="gold" style={{ fontSize: 11 }}>{tag.type}</Tag>
                    <Text style={{ fontSize: 12 }}>
                        {tag.value?.from ? `${tag.value.from} → ` : ''}{tag.value?.to || ''}
                    </Text>
                    <Popconfirm
                        title="Delete this tag?"
                        onConfirm={() => deleteTag(tag.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </div>
            ))}
            <Divider style={{ margin: '8px 0' }} />
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Select value={newType} onChange={setNewType} style={{ width: '100%' }}>
                    {TAG_TYPES.map((t) => (
                        <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                </Select>
                {newType === 'user-input-transform' && (
                    <Input placeholder="From" value={newFrom} onChange={(e) => setNewFrom(e.target.value)} />
                )}
                <Space.Compact style={{ width: '100%' }}>
                    <Input placeholder="To / value" value={newTo} onChange={(e) => setNewTo(e.target.value)} style={{ flex: 1 }} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>Add</Button>
                </Space.Compact>
            </Space>
        </div>
    );
};

// ── Create Module Form ────────────────────────────────
const CreateModuleForm = ({ onCancel }) => {
    const [form] = Form.useForm();
    const { createModule, loading, modules, emailGroups = [] } = useFlowStore();

    const replyType = Form.useWatch('reply_type', form);

    const handleCreate = async () => {
        try {
            const values = await form.validateFields();
            await createModule({
                title: values.title,
                reply_type: values.reply_type || '',
                response: values.response,
                field: values.field || '',
                options: values.options ? values.options.split(',').map((s) => s.trim()).filter(Boolean) : null,
                quick_replies: values.quick_replies ? values.quick_replies.split(',').map((s) => s.trim()).filter(Boolean) : null,
                error_response: values.error_response || '',
                fallback: values.fallback || null,
                email_groups: values.email_groups || null,
                response_append: values.response_append || null,
                response_user_modify: values.response_user_modify || false,
                is_terminal: values.is_terminal || false,
            });
            // Try to save the layout positions simultaneously 
            window.dispatchEvent(new Event('save-module-group-layout'));
            form.resetFields();
        } catch (err) {
            // Validation failed
        }
    };

    return (
        <div className="detail-panel-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <Title level={5} style={{ margin: 0 }}>
                    <PlusOutlined style={{ marginRight: 6 }} />
                    Create Module
                </Title>
                {onCancel && (
                    <Button type="text" size="small" onClick={onCancel}>Cancel</Button>
                )}
            </div>
            <Form form={form} layout="vertical">
                <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                    <Input placeholder="Module title" />
                </Form.Item>
                <Form.Item name="reply_type" label="Reply Type">
                    <Select placeholder="Select type" allowClear>
                        {REPLY_TYPES.map((rt) => (
                            <Option key={rt.value} value={rt.value}>{rt.label}</Option>
                        ))}
                    </Select>
                </Form.Item>
                {replyType === 'send-details-grp' && (
                    <Form.Item name="email_groups" label="Email Groups">
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Select email group(s)..."
                            notFoundContent={emailGroups.length ? undefined : 'No email groups available'}
                        >
                            {emailGroups.map((g) => (
                                <Option key={g.id} value={g.id}>{g.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
                <Form.Item name="response" label="Response" rules={[{ required: true }]}>
                    <TextArea rows={3} placeholder="Message sent to contact..." />
                </Form.Item>
                <Form.Item name="field" label="Field">
                    <Input placeholder="Contact field to save (e.g. email, zipcode)" />
                </Form.Item>
                <Form.Item name="response_append" label="Response Append">
                    <TextArea rows={2} placeholder="Text to append to response..." />
                </Form.Item>
                <Form.Item name="response_user_modify" valuePropName="checked">
                    <Switch checkedChildren="User Can Modify" unCheckedChildren="No Modification" />
                </Form.Item>
                <Form.Item name="options" label="Options (comma-separated)">
                    <Input placeholder="opt1, opt2, opt3" />
                </Form.Item>
                <Form.Item name="quick_replies" label="Quick Replies (comma-separated)">
                    <Input placeholder="yes, no, maybe" />
                </Form.Item>
                <Form.Item name="error_response" label="Error Response">
                    <TextArea rows={2} placeholder="Message when input is invalid..." />
                </Form.Item>
                <Form.Item
                    name="is_terminal"
                    valuePropName="checked"
                    label="Completion"
                    tooltip="Reaching this step marks the conversation as completed, even if it still has connections (e.g. a “Thank you” step that accepts “reschedule”). Without this, a flow only completes on a step with no exits."
                >
                    <Switch checkedChildren="Marks flow completed" unCheckedChildren="Not a completion step" />
                </Form.Item>
                <Form.Item name="fallback" label="Fallback Module">
                    <Select placeholder="Select fallback module..." allowClear>
                        <Option value={null}>None</Option>
                        {modules.map((m) => (
                            <Option key={m.id} value={m.id}>{m.title}</Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                        loading={loading}
                        block
                    >
                        Create Module
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

// ── Edit Module Panel ─────────────────────────────────
const EditModulePanel = ({ module }) => {
    const [form] = Form.useForm();
    const {
        updateModule, deleteModule, setStartModule,
        moduleGroup, bot, context, modules, loading,
        emailGroups = [],
    } = useFlowStore();

    const replyType = Form.useWatch('reply_type', form);

    const startModuleId = context === 'bot' ? bot?.start_module : moduleGroup?.module_id;
    const isStartModule = !!startModuleId && startModuleId === module.id;

    useEffect(() => {
        form.setFieldsValue({
            title: module.title,
            reply_type: module.reply_type || '',
            response: module.response || '',
            field: module.field || '',
            options: module.options ? module.options.join(', ') : '',
            quick_replies: module.quick_replies ? module.quick_replies.join(', ') : '',
            error_response: module.error_response || '',
            fallback: module.fallback || undefined,
            email_groups: parseEmailGroups(module.email_groups),
            response_append: module.response_append || '',
            response_user_modify: module.response_user_modify || false,
            is_terminal: module.is_terminal || false,
        });
    }, [module, form]);

    const handleUpdate = async () => {
        try {
            const values = await form.validateFields();
            await updateModule(module.id, {
                title: values.title,
                reply_type: values.reply_type || '',
                response: values.response,
                field: values.field || '',
                options: values.options ? values.options.split(',').map((s) => s.trim()).filter(Boolean) : null,
                quick_replies: values.quick_replies ? values.quick_replies.split(',').map((s) => s.trim()).filter(Boolean) : null,
                error_response: values.error_response || '',
                fallback: values.fallback || null,
                email_groups: values.email_groups || [],
                followups: JSON.stringify(module.followups || []),
                response_append: values.response_append || null,
                response_user_modify: values.response_user_modify || false,
                is_terminal: values.is_terminal || false,
            });
            // Try to save the layout positions simultaneously
            window.dispatchEvent(new Event('save-module-group-layout'));
        } catch (err) {
            // validation
        }
    };

    return (
        <div className="detail-panel-content">
            <div className="detail-panel-header">
                <Title level={5} style={{ margin: 0 }}>
                    {isStartModule && <StarOutlined style={{ color: '#52c41a', marginRight: 6 }} />}
                    {module.title}
                </Title>
                <Tag style={{ fontSize: 10 }}>{module.id.slice(0, 8)}...</Tag>
            </div>

            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="reply_type" label="Reply Type">
                    <Select allowClear>
                        {REPLY_TYPES.map((rt) => (
                            <Option key={rt.value} value={rt.value}>{rt.label}</Option>
                        ))}
                    </Select>
                </Form.Item>
                {replyType === 'send-details-grp' && (
                    <Form.Item name="email_groups" label="Email Groups">
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Select email group(s)..."
                            notFoundContent={emailGroups.length ? undefined : 'No email groups available'}
                        >
                            {emailGroups.map((g) => (
                                <Option key={g.id} value={g.id}>{g.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
                <Form.Item name="response" label="Response" rules={[{ required: true }]}>
                    <TextArea rows={3} />
                </Form.Item>
                <Form.Item name="field" label="Field">
                    <Input placeholder="Contact field" />
                </Form.Item>
                <Form.Item name="response_append" label="Response Append">
                    <TextArea rows={2} />
                </Form.Item>
                <Form.Item name="response_user_modify" valuePropName="checked">
                    <Switch checkedChildren="User Can Modify" unCheckedChildren="No Modification" />
                </Form.Item>
                <Form.Item name="options" label="Options">
                    <Input placeholder="Comma-separated options" />
                </Form.Item>
                <Form.Item name="quick_replies" label="Quick Replies">
                    <Input placeholder="Comma-separated" />
                </Form.Item>
                <Form.Item name="error_response" label="Error Response">
                    <TextArea rows={2} />
                </Form.Item>
                <Form.Item
                    name="is_terminal"
                    valuePropName="checked"
                    label="Completion"
                    tooltip="Reaching this step marks the conversation as completed, even if it still has connections (e.g. a “Thank you” step that accepts “reschedule”). Without this, a flow only completes on a step with no exits."
                >
                    <Switch checkedChildren="Marks flow completed" unCheckedChildren="Not a completion step" />
                </Form.Item>
                <Form.Item name="fallback" label="Fallback Module">
                    <Select placeholder="Select fallback..." allowClear>
                        <Option value={null}>None</Option>
                        {modules.filter(m => m.id !== module.id).map((m) => (
                            <Option key={m.id} value={m.id}>{m.title}</Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>

            <Space style={{ width: '100%', marginBottom: 20 }} wrap>
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleUpdate}
                    loading={loading}
                >
                    Update
                </Button>
                {!isStartModule && (
                    <Button
                        icon={<StarOutlined />}
                        onClick={() => setStartModule(module.id)}
                    >
                        Set as Start
                    </Button>
                )}
                <Popconfirm
                    title="Delete this module? This will also delete all connections."
                    onConfirm={() => deleteModule(module.id)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Button danger icon={<DeleteOutlined />}>Delete</Button>
                </Popconfirm>
            </Space>

            <Collapse
                size="small"
                defaultActiveKey={['connections']}
                items={[
                    {
                        key: 'connections',
                        label: `Connections (${(module.connection || []).length})`,
                        children: <ConnectionsSection module={module} modules={modules} />,
                    },
                    {
                        key: 'triggers',
                        label: `Triggers (${(module.triggers || []).filter((t) => !t.deleted_at).length})`,
                        children: <TriggersSection module={module} modules={modules} />,
                    },
                    {
                        key: 'followups',
                        label: `Follow-ups (${(module.followups || []).length})`,
                        children: <FollowupsSection module={module} />,
                    },
                    ...(context === 'bot' ? [
                        {
                            key: 'path_forks',
                            label: `Location Forks (${(module.path_forks || []).length})`,
                            children: <PathForksSection module={module} modules={modules} />,
                        },
                        {
                            key: 'bot_redirect',
                            label: `Bot Redirects (${(module.bot_redirect || []).length})`,
                            children: <BotRedirectsSection module={module} />,
                        },
                        {
                            key: 'tags',
                            label: `Tags (${(module.tags || []).length})`,
                            children: <TagsSection module={module} />,
                        },
                    ] : []),
                ]}
            />
        </div>
    );
};

// ── Module Group Info Panel (name + description editing) ──
const ModuleGroupInfoPanel = ({ onCreateModule }) => {
    const {
        moduleGroup,
        updateModuleGroupName,
        updateModuleGroupDescription,
    } = useFlowStore();

    const [form] = Form.useForm();

    useEffect(() => {
        if (moduleGroup) {
            form.setFieldsValue({
                name: moduleGroup.name || '',
                description: moduleGroup.description || '',
            });
        }
    }, [moduleGroup, form]);

    const handleSave = async () => {
        const values = form.getFieldsValue();
        const nameChanged = values.name?.trim() && values.name.trim() !== moduleGroup.name;
        const descChanged = (values.description || '') !== (moduleGroup.description || '');

        if (nameChanged) await updateModuleGroupName(values.name.trim());
        if (descChanged) await updateModuleGroupDescription(values.description || '');
    };

    if (!moduleGroup) return null;

    return (
        <div className="detail-panel-content">
            <Title level={5} style={{ margin: '0 0 16px' }}>Module Group Info</Title>
            <Form form={form} layout="vertical">
                <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                    <Input placeholder="Module group name" />
                </Form.Item>
                <Form.Item name="description" label="Description">
                    <Input.TextArea rows={3} placeholder="Describe what this module group does..." />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} block>
                        Save
                    </Button>
                </Form.Item>
            </Form>
            <Divider />
            <Button type="primary" icon={<PlusOutlined />} onClick={onCreateModule} block>
                Create New Module
            </Button>
        </div>
    );
};

// ── Bot Info Panel (bot context only) ─────────────────
const BotInfoPanel = ({ onCreateModule }) => {
    const { bot } = useFlowStore();

    if (!bot) {
        return (
            <div className="detail-panel-content">
                <Text type="secondary">Select a bot to start editing its flow.</Text>
            </div>
        );
    }

    return (
        <div className="detail-panel-content">
            <Title level={5} style={{ margin: '0 0 8px' }}>{bot.name}</Title>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {(bot.botmap && bot.botmap[0]?.type) || bot.type || '—'}
                {bot.botmap && bot.botmap[0]?.mobile ? ` · ${bot.botmap[0].mobile}` : ''}
            </Text>
            <Divider />
            <Button type="primary" icon={<PlusOutlined />} onClick={onCreateModule} block>
                Create New Module
            </Button>
        </div>
    );
};

// ── Main Detail Panel ─────────────────────────────────
const ModuleDetailPanel = ({ InfoPanel }) => {
    const { selectedModuleId, modules, context } = useFlowStore();
    const [showCreate, setShowCreate] = useState(false);

    const selectedModule = selectedModuleId
        ? modules.find((m) => m.id === selectedModuleId)
        : null;

    useEffect(() => {
        if (selectedModule) setShowCreate(false);
    }, [selectedModule]);

    if (selectedModule) {
        return <EditModulePanel module={selectedModule} />;
    }

    if (showCreate) {
        return <CreateModuleForm onCancel={() => setShowCreate(false)} />;
    }

    if (InfoPanel) {
        return <InfoPanel onCreateModule={() => setShowCreate(true)} />;
    }

    if (context === 'bot') {
        return <BotInfoPanel onCreateModule={() => setShowCreate(true)} />;
    }

    return <ModuleGroupInfoPanel onCreateModule={() => setShowCreate(true)} />;
};

export default ModuleDetailPanel;
